import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { getEnvFlag } from 'common/utils/env';
import { worker } from 'common/mockServiceWorker/worker';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { initSentry } from 'common/utils/sentry';
import App, { ErrorFallback } from './app';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';

// Initialize Sentry error tracking FIRST (before any other code)
initSentry();

// Lets index.css drop the body's fixed background on native. In the iOS
// webview a fixed background is repainted on every viewport resize, and the
// Capacitor keyboard resizes the viewport natively on every show/hide, which
// adds to the judder under bottom sheets while typing.
if (Capacitor.isNativePlatform()) {
  document.body.classList.add('native-platform');
}

// Suppress the benign ResizeObserver loop warning. Browsers fire this when a
// ResizeObserver callback causes a layout change in the same tick — it doesn't
// indicate a real failure but CRA's react-error-overlay surfaces it as a fatal
// error. The overlay registers its own error handler, so we use capture phase
// to intercept first and stop propagation before it reaches them.
const isResizeObserverError = (msg: string): boolean => (
  msg === 'ResizeObserver loop limit exceeded'
  || msg === 'ResizeObserver loop completed with undelivered notifications.'
);

window.addEventListener(
  'error',
  (e) => {
    if (isResizeObserverError(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

// react-error-overlay also listens for unhandledrejection events.
window.addEventListener(
  'unhandledrejection',
  (e) => {
    const reason = (e.reason && e.reason.message) || '';
    if (isResizeObserverError(reason)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true,
);

if (getEnvFlag('REACT_APP_SHOULD_RUN_MSW')) {
  worker.start({ onUnhandledRequest: 'bypass' });
}

const container = document.getElementById('root');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);
// The boundary INSIDE app.tsx is rendered by App itself, so it only ever
// covered App's children - never App's own body or its mount effects. Those
// effects configure RevenueCat, Adjust and Mixpanel on launch, so anything
// throwing in there had no boundary above it and went straight to a white
// screen with no fallback and nothing reported. This one sits above App and
// closes that gap; the inner one stays as it is.
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// Unregister service worker to ensure users always get the latest version
// The service worker's cache-first strategy can cause stale content issues
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
